import React, { useState, useMemo } from 'react';
import { FORMS } from '../../schemas/stepMap';
import FormBuilder from '../../components/formBuilder';
import { useOutletContext } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import BackForwardButtons from '../../components/buttons/BackForwardButtons';
import { useSteps } from '../../hooks/useSteps';
import { useLocation } from 'react-router-dom';
import { Link } from 'react-router-dom';
import graphDataFormatter from './Comparison/graphDataFormatter';
import { projectionCalculator } from './Comparison/projectionCalculator';
import SummaryProjection from './SummaryProjection';
import { useUser } from '../../hooks/useUser';
import InvestmentMetrics from '../../components/InvestmentMetrics';

const graphSelectOptions = [
  {
    name: 'monthlyNOI',
    description: 'New Monthly Income After Operating Expenses',
    label: 'Net Income',
  },
  {
    name: 'totalEquity',
    description:
      'Total Equity Based on the Estimated Market Value of the Property',
    label: 'Total Equity',
  },
  {
    name: 'monthlyPreTaxCashFlowBuyAndHold',
    description: 'Annual Pre-Tax Cash Flow',
    label: 'Cash Flow',
  },
  {
    name: 'totalROIYear1BuyandHold',
    description: 'Return On Investment',
    label: 'ROI',
  },
  { name: 'propertyValueProjection', description: '', label: 'Property Value' },
  {
    type: 'stack',
    stackIncludes: [
      { name: 'yearOnePrincipalReduction', label: 'Principal' },
      {
        name: 'interest',
        label: 'Interest',
        derivedFrom: [
          'monthlyDebtService',
          'balloonPayment',
          'yearOnePrincipalReduction',
        ],
        calcFunction: (
          monthlyDebtService,
          balloonPayment,
          yearOnePrincipalReduction
        ) =>
          monthlyDebtService +
          (balloonPayment || 0) -
          yearOnePrincipalReduction,
      },
    ],
    description: 'Mortgage Payments over the years',
    label: 'Amortization',
  },
];

export default function BuyAndHoldSummary() {
  const { propertyData, scenarios } = useOutletContext();
  const scenarioData = scenarios.find((scenario) => !scenario.isDefault);
  const { user, userDocData } = useUser();

  const { pathname } = useLocation();

  if (!scenarioData?.rehabWorksheetSubmitted) {
    return (
      <div className="w-full flex flex-col items-center justify-center mt-36">
        <h2 className="mb-6 text-xl font-medium text-primary">
          No Buy and Hold Analysis Created
        </h2>
        {!pathname.includes('sharing') && (
          <p className="mb-72 w-2/3 text-center">
            Once you have filled out the{' '}
            <Link
              to="../../purchase-worksheet"
              className="font-medium text-primary hover:text-primary-700 focus:text-primary-700"
            >
              purchase worksheet{' '}
            </Link>
            and the{' '}
            <Link
              to="../../rehab-worksheet"
              className="font-medium text-primary hover:text-primary-700 focus:text-primary-700"
            >
              rehab worksheet
            </Link>{' '}
            you will be able to see your buy and hold analysis here.
          </p>
        )}
      </div>
    );
  }

  const { prev, curr, next } = useSteps();

  const BuyAndHoldForm = curr || FORMS.BuyAndHoldAnalysis;
  const BuyAndHoldSchema = BuyAndHoldForm.schema;

  const [graph, setGraph] = useState(0);
  const [years, setYears] = useState(30);

  const projectionForm = FORMS.projection;

  const projectionSchema = projectionForm.schema.calculations.schema[2];
  const projectionFields = projectionSchema.schema;

  const projectionData = useMemo(
    () =>
      projectionCalculator(years, projectionFields, {
        ...propertyData,
        ...scenarioData,
      }),
    [years, propertyData, scenarioData]
  );

  const graphData = graphDataFormatter(
    years,
    projectionData,
    {},
    {},
    graphSelectOptions[graph]?.name,
    null,
    null,
    'single',
    graphSelectOptions[graph]
  );

  const rehabYears = scenarioData?.rehabLength
    ? Math.floor(scenarioData?.rehabLength / 12)
    : 0;

  return (
    <>
      <Helmet>
        <title>PropertyCast.io - Buy and Hold Analysis</title>
      </Helmet>
      <div>
        {user && !userDocData.loading && (
          <InvestmentMetrics
            schema={BuyAndHoldForm.investmentMetrics}
            data={{ ...userDocData, ...propertyData, ...scenarioData }}
          />
        )}
      </div>
      <div className="pt-4 pb-2">
        <FormBuilder
          formType={BuyAndHoldForm.formType}
          calculationsSchema={BuyAndHoldSchema}
          beforeData={propertyData}
          afterData={scenarioData}
          keyMetricsSchema={BuyAndHoldForm.keyMetrics}
          keyMetricsData={{ ...propertyData, ...scenarioData }}
        />

        <div>
          <SummaryProjection
            options={graphSelectOptions}
            currOption={graph}
            setOption={setGraph}
            years={years}
            setYears={setYears}
            graphData={graphData}
            tableData={projectionData}
            schema={projectionSchema}
            rehabYears={rehabYears}
          />
        </div>
      </div>
      <BackForwardButtons prev={prev} next={next} />
    </>
  );
}
